import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Image, 
  Video, 
  Edit,
  Trash2,
  Plus,
  FileText,
  Mail,
  Clock,
  BookOpen,
  PenTool
} from 'lucide-react';
import config from '../utils/config';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const API_URL = config.url;
const AdminDashboard = () => {
  const [stats, setStats] = useState({
    events: 0,
    gallery: 0,
    videos: 0,
    press: 0,
    contacts: 0,
    appointments: 0,
    rachnaye: 0,
    blog: 0
  });
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentGallery, setRecentGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [eventsRes, galleryRes, videosRes, pressRes, contactsRes, appointmentsRes, rachnayeRes, blogRes] = await Promise.all([
        fetch(`${API_URL}/events`),
        fetch(`${API_URL}/gallery`),
        fetch(`${API_URL}/videos`),
        fetch(`${API_URL}/press`),
        fetch(`${API_URL}/contacts`),
        fetch(`${API_URL}/appointments`),
        fetch(`${API_URL}/rachnaye`),
        fetch(`${API_URL}/blog`)
      ]);

      const events = await eventsRes.json();
      const gallery = await galleryRes.json();
      const videos = await videosRes.json();
      const press = await pressRes.json();
      const contacts = await contactsRes.json();
      let appointments = await appointmentsRes.json();
      if (!Array.isArray(appointments) && appointments && Array.isArray(appointments.appointments)) appointments = appointments.appointments;
      const rachnaye = await rachnayeRes.json();
      const blog = await blogRes.json();

      setStats({
        events: events.length,
        gallery: gallery.length,
        videos: videos.length,
        press: press.length,
        contacts: contacts.length,
        appointments: appointments.length,
        rachnaye: rachnaye.length,
        blog: blog.length
      });

      setRecentEvents(events.slice(0, 3));
      setRecentGallery(gallery.slice(0, 3));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Events',
      value: stats.events,
      icon: Calendar,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      route: '/admin/events'
    },
    {
      title: 'Gallery Items',
      value: stats.gallery,
      icon: Image,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      route: '/admin/gallery'
    },
    {
      title: 'Videos',
      value: stats.videos,
      icon: Video,
      color: 'bg-red-500',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      route: '/admin/videos'
    },
    {
      title: 'Press',
      value: stats.press,
      icon: FileText,
      color: 'bg-pink-500',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-600',
      route: '/admin/press'
    },
    {
      title: 'Total Contacts',
      value: stats.contacts,
      icon: Mail,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      route: '/admin/contacts'
    },
    {
      title: 'Total Appointments',
      value: stats.appointments,
      icon: Clock,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600',
      route: '/admin/appointments'
    },
    {
      title: 'Poetry',
      value: stats.rachnaye,
      icon: BookOpen,
      color: 'bg-teal-500',
      bgColor: 'bg-teal-50',
      textColor: 'text-teal-600',
      route: '/admin/rachnaye'
    },
    {
      title: 'Blog',
      value: stats.blog,
      icon: PenTool,
      color: 'bg-cyan-500',
      bgColor: 'bg-cyan-50',
      textColor: 'text-cyan-600',
      route: '/admin/blog'
    }
  ];

  // Add handlers for edit/delete for events and gallery
  const handleEditEvent = (event) => {
    navigate('/admin/events', { state: { editId: event.id } });
  };
  const handleDeleteEvent = async (event) => {
    const result = await Swal.fire({
      title: 'Delete this event?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/events/${event.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchDashboardData();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to delete event',
          text: 'Please try again.',
          confirmButtonColor: '#f97316'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Network error',
        text: 'Please try again.',
        confirmButtonColor: '#f97316'
      });
    }
  };
  const handleEditGallery = (item) => {
    navigate('/admin/gallery', { state: { editId: item.id } });
  };
  const handleDeleteGallery = async (item) => {
    const result = await Swal.fire({
      title: 'Delete this image?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/gallery/${item.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchDashboardData();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to delete image',
          text: 'Please try again.',
          confirmButtonColor: '#f97316'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Network error',
        text: 'Please try again.',
        confirmButtonColor: '#f97316'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Welcome to Admin Dashboard</h1>
        <p className="text-orange-100">Manage your events, gallery, and videos from one place</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer hover:scale-[1.03] hover:brightness-95 duration-150"
              onClick={() => navigate(stat.route)}
              tabIndex={0}
              role="button"
              aria-label={`Go to ${stat.title}`}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(stat.route); }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.bgColor} p-3 rounded-lg`}>
                  <Icon className={`w-6 h-6 ${stat.textColor}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
  {/* Quick Actions */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center space-x-3 p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors" onClick={() => navigate('/admin/events')}>
            <Plus className="w-5 h-5 text-orange-600" />
            <span className="font-medium text-orange-700">Add New Event</span>
          </button>
          <button className="flex items-center space-x-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors" onClick={() => navigate('/admin/gallery')}>
            <Plus className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-purple-700">Upload Image</span>
          </button>
          <button className="flex items-center space-x-3 p-4 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" onClick={() => navigate('/admin/videos')}>
            <Plus className="w-5 h-5 text-red-600" />
            <span className="font-medium text-red-700">Add Video</span>
          </button>
        </div>
      </div>
      {/* Recent Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Events */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Events</h3>
            <button className="text-orange-600 hover:text-orange-700 text-sm font-medium" onClick={() => navigate('/admin/events')}>
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentEvents.length > 0 ? (
              recentEvents.map((event) => (
                <div key={event.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                  {event.image_url && (
                    <img 
                      src={`${API_URL}${event.image_url}`} 
                      alt={event.title}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{event.title}</h4>
                    <p className="text-sm text-gray-500">{event.date}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="p-1 text-blue-600 hover:bg-blue-50 rounded" onClick={() => handleEditEvent(event)}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="p-1 text-red-600 hover:bg-red-50 rounded" onClick={() => handleDeleteEvent(event)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No events yet</p>
                <button className="mt-2 text-orange-600 hover:text-orange-700 text-sm font-medium">
                  Add your first event
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Recent Gallery */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Gallery</h3>
            <button className="text-orange-600 hover:text-orange-700 text-sm font-medium" onClick={() => navigate('/admin/gallery')}>
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentGallery.length > 0 ? (
              recentGallery.map((item) => (
                <div key={item.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                  {item.image_url && (
                    <img 
                      src={`${API_URL}${item.image_url}`} 
                      alt={item.title}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{item.title}</h4>
                    <p className="text-sm text-gray-500">{item.category}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="p-1 text-blue-600 hover:bg-blue-50 rounded" onClick={() => handleEditGallery(item)}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="p-1 text-red-600 hover:bg-red-50 rounded" onClick={() => handleDeleteGallery(item)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Image className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No gallery items yet</p>
                <button className="mt-2 text-orange-600 hover:text-orange-700 text-sm font-medium">
                  Add your first image
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

    
    </div>
  );
};

export default AdminDashboard; 